import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { statisticsService, DailyRestaurantStatisticsResponse } from '../../services/statisticsService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

type DateRangePreset = 'today' | 'last7days' | 'thisWeek' | 'thisMonth' | 'last30days';

interface RestaurantStatisticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  restaurantName: string;
}

const RestaurantStatisticsModal: React.FC<RestaurantStatisticsModalProps> = ({
  isOpen,
  onClose,
  restaurantId,
  restaurantName
}) => {
  const { t } = useTranslation();

  const [statisticsData, setStatisticsData] = useState<DailyRestaurantStatisticsResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('last7days');

  const getDateRange = (preset: DateRangePreset): { from: string; to: string } => {
    const now = new Date();

    const getStartOfDay = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}T00:00:00Z`;
    };

    const getEndOfDay = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}T23:59:59Z`;
    };

    let fromDate: string;
    let toDate: string = getEndOfDay(now);

    switch (preset) {
      case 'today':
        fromDate = getStartOfDay(now);
        break;
      case 'last7days':
        const last7Start = new Date(now);
        last7Start.setDate(last7Start.getDate() - 6);
        fromDate = getStartOfDay(last7Start);
        break;
      case 'thisWeek':
        const dayOfWeek = now.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const monday = new Date(now);
        monday.setDate(monday.getDate() - daysFromMonday);
        fromDate = getStartOfDay(monday);
        break;
      case 'thisMonth':
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        fromDate = getStartOfDay(firstDay);
        break;
      case 'last30days':
        const last30Start = new Date(now);
        last30Start.setDate(last30Start.getDate() - 29);
        fromDate = getStartOfDay(last30Start);
        break;
    }

    return {
      from: fromDate,
      to: toDate
    };
  };

  const fetchStatistics = async (preset: DateRangePreset) => {
    setLoading(true);
    setError('');

    try {
      const dateRange = getDateRange(preset);
      const response = await statisticsService.getRestaurantPerDay({
        ...dateRange,
        restaurantId
      });

      if (response.success && response.data) {
        setStatisticsData(response.data);
      } else {
        setError(response.error || 'Failed to fetch restaurant statistics');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && restaurantId) {
      fetchStatistics(selectedPreset);
    }
  }, [isOpen, restaurantId, selectedPreset]);

  const handlePresetChange = (preset: DateRangePreset) => {
    setSelectedPreset(preset);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString()} soum`;
  };

  const getPresetLabel = (preset: DateRangePreset): string => {
    switch (preset) {
      case 'today':
        return t('statistics.today');
      case 'last7days':
        return t('statistics.last7days');
      case 'thisWeek':
        return t('statistics.thisWeek');
      case 'thisMonth':
        return t('statistics.thisMonth');
      case 'last30days':
        return t('statistics.last30days');
    }
  };

  const statisticsChartData = (statisticsData || []).map(item => ({
    date: formatDate(item.date),
    ordersCount: item.ordersCount || 0,
    foodPrice: item.totalFoodPrice || 0,
    profit: item.restaurantProfit || 0
  }));

  const getTotalOrders = () => {
    if (!statisticsData || statisticsData.length === 0) return 0;
    return statisticsData.reduce((sum, item) => sum + (item.ordersCount || 0), 0);
  };

  const getTotalFoodPrice = () => {
    if (!statisticsData || statisticsData.length === 0) return 0;
    return statisticsData.reduce((sum, item) => sum + (item.totalFoodPrice || 0), 0);
  };

  const getTotalProfit = () => {
    if (!statisticsData || statisticsData.length === 0) return 0;
    return statisticsData.reduce((sum, item) => sum + (item.restaurantProfit || 0), 0);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('statistics.restaurantStatistics')} - ${restaurantName}`}
      maxWidth="1000px"
    >
      <div>
        {/* Date Range Presets */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            {(['today', 'last7days', 'thisWeek', 'thisMonth', 'last30days'] as DateRangePreset[]).map((preset) => (
              <button
                key={preset}
                onClick={() => handlePresetChange(preset)}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: `2px solid ${selectedPreset === preset ? '#17a2b8' : '#ddd'}`,
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  backgroundColor: selectedPreset === preset ? '#17a2b8' : '#fff',
                  color: selectedPreset === preset ? '#fff' : '#333',
                  fontWeight: selectedPreset === preset ? 600 : 500,
                  transition: 'all 0.2s ease',
                  opacity: loading ? 0.6 : 1,
                  fontSize: '0.95rem'
                }}
              >
                {getPresetLabel(preset)}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {t('statistics.loading')}
          </div>
        ) : statisticsData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {t('statistics.noData')}
          </div>
        ) : (
          <>
            <div style={{ width: '100%', height: 400, marginTop: '1rem', minHeight: 400 }}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={statisticsChartData}
                  margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '10px'
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="ordersCount"
                    stroke="#007bff"
                    strokeWidth={3}
                    dot={{ fill: '#007bff', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.ordersCount')}
                  />
                  <Line
                    type="monotone"
                    dataKey="foodPrice"
                    stroke="#28a745"
                    strokeWidth={3}
                    dot={{ fill: '#28a745', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.foodPrice')}
                  />
                  <Line
                    type="monotone"
                    dataKey="profit"
                    stroke="#17a2b8"
                    strokeWidth={3}
                    dot={{ fill: '#17a2b8', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.restaurantProfit')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Summary Cards */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginTop: '1.5rem'
            }}>
              <div style={{
                backgroundColor: '#e7f3ff',
                borderLeft: '4px solid #007bff',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalRestaurantOrders')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                  {getTotalOrders()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#d4edda',
                borderLeft: '4px solid #28a745',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalRestaurantFoodPrice')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                  {formatPrice(getTotalFoodPrice())}
                </div>
              </div>

              <div style={{
                backgroundColor: '#d1ecf1',
                borderLeft: '4px solid #17a2b8',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalRestaurantProfit')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#17a2b8' }}>
                  {formatPrice(getTotalProfit())}
                </div>
              </div>

              <div style={{
                backgroundColor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.daysTracked')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
                  {statisticsData.length}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default RestaurantStatisticsModal;
